/*========================================================================
* $Id$
*
* Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2013
*
* Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
* Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
* Urheber festgelegten Stelle in der Funktion des Computerprogramms
* angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
* Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
* Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
* =========================================================================*/

define([
  "underscore",
  "backbone",
  "utils/dom",
  "utils/jsonpath",
  "i18n/i18n",
  "jquery",
  "model/attribute_proxy",
  "utils/object",
  "utils/properties",
  "client/schema"
], function (
  _,
  Backbone,
  dom,
  JSONPath,
  i18n,
  $,
  AttributeProxyModel,
  MBObject,
  PropertyUtils,
  Schema
) {

  var AttributeCollection = Backbone.Collection.extend({

    initialize: function (models, options) {
      Backbone.Collection.prototype.initialize.apply(this, arguments);
      this.template = options.template;
      this.selectorPattern = options.selectorPattern;
      this.listenTo(this.template, "change", this.updateModels);
      this.updateModels();
    },

    updateModels: function () {
      var that = this,
          index = 0;

      _.each(this.template.schema.attributes, function (attribute, id) {
        var selectorPattern = that.selectorPattern || ".*";
        var selector = attribute.selector || "global";
        var selectorVisible = new RegExp(selectorPattern).test(selector);
        var dependencyOKAndVisible = selectorVisible && attribute.visible !== false && (!attribute.depends || that.evalExpr(attribute.depends, id));
    
        var visibleWithinSearchcontainer = that.isVisibleWithinSearchcontainer(attribute);
        if (dependencyOKAndVisible && visibleWithinSearchcontainer) {
          that.add({id: id, attribute: attribute}, {at: index});
          index++;
        } else {
          that.get(id) && that.get(id).trigger("destroy", that.get(id));
        }
      });
    },

    isVisibleWithinSearchcontainer: function (attribute) {
      var hiddenInSearchcontainer = attribute.hiddenWithinSearchcontainerView === true;
      if (hiddenInSearchcontainer) {
        // check if is child of searchcontainer view
        var isHidden = false;
        var viewAttr = "data-searchcontainer-view";
        var template = this.template;
        while (template !== undefined) {
          if (template.el.hasAttribute(viewAttr)) {
            isHidden = true;
          }
          template = template.parentTemplate;
        }
        if (isHidden) return false;
      }
      return true;
    },

    getParentTemplate: function (template) {
      return template.parentTemplate;
    },

    evalExpr: function (expr, id) {
      var f = new Function("attributes", "return " + expr);
      return !!f.call(this, this.template.getAttributes());
    },
  });

  var Template = function (el, node, application, settingsConfig) {
    this.el = el;
    this.node = node;
    this.application = application;
    this.settingsConfig = settingsConfig;

    if (node) {
      if (node.parentNode) {
        this.parentTemplate = node.parentNode.template;
      }
      this.children       = node.children;
    } else {
      this.children = [];
    }
    this.instances = new Instances();
    // this.attributes = {};
    this.content = {
      properties: []
    };
    this.initialize(node);
  };

  var Instances = function () {
    this.instances = [];
    this.length = 0;
  }

  Instances.prototype = {

    push: function (view) {
      if (!view) return;

      var that = this,
          originalRemove = view.remove;

      view.remove = function () {
        that.instances = _.without(that.instances, this);
        that.length = that.instances.length;
        originalRemove.apply(this, arguments);
        view.remove = originalRemove;
      };

      this.instances.push(view);

      this.length = this.instances.length;
    },

    remove: function () {
      _.forEach(this.instances, function(view) {
        if (view && view.remove) view.remove();
      });
      this.instances = [];

      this.length = this.instances.length;
    }
  }

  _.extend(Template.prototype, Backbone.Events, {

    initialize: function (node, tree) {
      if (!this.attributeModel) {
        if (!this.el || this.el == document) {
          this.attributeModel = new dom.EmptyModel();
        }
        else {
          this.attributeModel = new dom.AttributeModel(this.el, "data-");
        }
      }
      this.listenTo(this.attributeModel, "all", this.proxyEvents);

      this.editable = !!this.schema && node && !node.readonly;
      if (
        this.application
        && this.application.additionalSchemaAttributes
        && typeof this.application.additionalSchemaAttributes === 'function'
      ) {
        if (this.schema && this.schema && this.application.additionalSchemaAttributes()) {
          var additionalAttrs = this.application.additionalSchemaAttributes()[this.node.templateType];
          if (additionalAttrs) {
            var additionalSchema = new Template.Schema( additionalAttrs );
            this.schema.addAdditionalAttrs(additionalSchema);
          }
        }
      }

      this.guardedChildren = this.findGuardedChildren();
      this.childrenPromise = this.createDefaultTemplateIfNonePresent();
    },

    findGuardedChildren: function() {
      if (!this.node) return [];

      return _.filter(
        this.node.children, 
        function(child) { return child.guard !== undefined; }
      );
    },

    getDisplayedProperties: function() {    
      if (this.node.children) {
        
        this.displayedProperties = _.reduce(this.node.children, function(mergedProperties, child) {
          if (child.template && child.template.getDisplayedProperties) {
            PropertyUtils.mergeProperties(mergedProperties, child.template.getDisplayedProperties());
          };  

          if (child.guard) {
            PropertyUtils.mergeProperties(mergedProperties, child.guard.requiredProperties)
          }

          return mergedProperties;
        }, {});

        return this.displayedProperties;
      }
    },

    getSchemaAttributeCollection: function (options) {
      if (this.schema) {
        return new AttributeCollection(null, { template: this, selectorPattern: options && options.selectorPattern });
      }
    },

    proxyEvents: function (eventType, target) {
      this.trigger.apply(this, [eventType, this].concat(Array.prototype.slice.call(arguments, 2)));
    },

    append: function () {
      if (!this.node) {
        return [];
      }
      return this.node.append.apply(this.node, arguments);
    },

    createDefaultTemplateIfNonePresent: function () {
      if (!this.isTemplatePresent()) {
        var defaultEl = this.getDefaultTemplateAsEl();
        if (!defaultEl) {
          return Promise.resolve([]);
        }

        return Promise.resolve(this.append(defaultEl));

      }
      return Promise.resolve(this.node && this.node.children || []);
    },

    getTemplateNodeHasChildren: function() {
      return this.node
        && this.node.children.length >= 1;
    },
    
    isTemplatePresent: function () {
      return !this.hasContent() ||
        this.getTemplateNodeHasChildren();
    },

    getDefaultTemplateAsEl: function () {
      var html = this.getDefaultTemplate();
      if (html) return dom.instantiate(html);
    },
    
    getDefaultTemplate: function () {
      return this.constructor.DEFAULT_TEMPLATE;
    },

    /* -------------------  computed path information ------ */

    name: function() {
      return i18n(this.editableName || this.attributeModel.get("template"));
    },

    pathSchema: function() {
      return {
        type: "string",
        title: "Model path",
        readonly: true
      };
    },

    parentPath: function() {
      if (this.parentTemplate && this.parentTemplate.path) {
        return this.parentTemplate.path();
      }
      return [];
    },

    model: function (options) {
      var model = options.model,
          modelName, localPath;

      if (this.attributeModel.has("model")) {
        modelName = this.attributeModel.get("model");
        model =  this.application.models[modelName];
      }

      if (this.attributeModel.has("model-transformation")) {
        var modelPropertyDefinitions = this.attributeModel.get("model-transformation");
        if (modelPropertyDefinitions && this.application[modelPropertyDefinitions]) {
          model = new AttributeProxyModel(model,
                                          this.application[modelPropertyDefinitions].call(this.application));
        }
      }

      localPath = this.localPath();
      if (model && model.submodel && localPath && localPath.length) {
        model = model.submodel(localPath);
      }

      return model;
    },

    /** get the path within the model */
    /** should be overrided in sub templates if not from  attributeModel */
    localPath: function() {
      if (this.attributeModel.has("path")) {
        return JSONPath.parsePath(this.attributeModel.get("path"));
      }
      return [];
    },

    path: function() {
      return this.parentPath().concat(this.localPath());
    },


    /* -------------------  attribute/content information ------ */

    /**  Structural values according to schema */
    set: function (key, value) {
      if (this.schema) {
        value = this.schema.serializeAttribute(value, key);
      }
      if (this.schema.useCamelCaseNames) {
        key = dom.AttributeModel.camelCaseToSeparated(key);
      }
      this.setAttribute(key, value);
    },

    get: function (key, defaultValue) {
      if (!key) {
        var atts = this.getAttributes();
        if (!this.schema) return atts;
        for (var name in atts) {
            atts[name] = this.schema.parseAttribute(atts[name], name);
        }
        return atts;
      }

      var v = this.getAttribute(key);
      if (!this.schema) {
        return v;
      }
      return this.schema.parseAttribute(v, key, defaultValue);
    },

    /** Returns the input model in parsed form */
    getInput: function () {
      var atts = this.schema.filterInputAttributes(this.getAttributes()),
          path;

      for (var name in atts) {
        path = this.schema.inputAttributes[name] && this.schema.inputAttributes[name].path || name;

        atts[path] = this.schema.parseAttribute(atts[name], name);

        if (path !== name) {
          delete atts[name];
        }
      }
      return atts;
    },

    /** Textual, serialized values (as written in html) */
    getAttribute: function(key) {
      if (this.schema.useCamelCaseNames) {
        key = dom.AttributeModel.camelCaseToSeparated(key);
      }
      return this.attributeModel.get(key);
    },

    hasAttribute: function(key) {
      if (this.schema.useCamelCaseNames) {
        key = dom.AttributeModel.camelCaseToSeparated(key);
      }
      return this.attributeModel.has(key);
    },

    removeAttribute: function(key) {
      if (this.schema.useCamelCaseNames) {
        key = dom.AttributeModel.camelCaseToSeparated(key);
      }
      this.attributeModel.set(key, null, {unset: true});
    },

    setAttribute: function(key, value) {
      if (this.schema.useCamelCaseNames) {
        key = dom.AttributeModel.camelCaseToSeparated(key);
      }
      this.attributeModel.set(key, value);
      return this;
    },

    hasContent: function() { return false; },

    getContent: function() {
      return  this.el.innerHTML;
    },

    setContent: function(value) {
      this.el.innerHTML = value;
    },

    getAttributes: function() {
      if (this.settingsConfig) {
        var templateName = this.getAttribute('template');
        var attrs = this.attributeModel.get();
        if (this.useCamelCaseNames) {
          attrs = this.attributeModel.getCamelCase();
        }
        
        // @TODO handle camel case attributes
        return this.settingsConfig.getEffectiveSettingsForTemplate(templateName, attrs);
      }
      
      // Fallback in case this.settingsConfig is not set
      if (this.useCamelCaseNames) {
        return this.attributeModel.getCamelCase();
      }
      return this.attributeModel.get();
    },

    getInputAttributes: function () {
      return this.schema.filterInputAttributes(this.attributeModel.get());
    },

    getOptions: function (options) {
      return options;
    },

    /** create the view accoring to the constructor of this.view */
    createView: function (options) {
      var ViewConstructor = options.ViewConstructor || this.constructor.View;
      var model = this.model(options);
      var options = this.getOptions(options);

      var viewOptions = _.extend(
        {},
        this.attributeModel.get(),
        options,
        {
          model: model,
          el: this.createEl(),
          template: this
        }
      )

      if (options.forceCreateNewElement && viewOptions.el == this.el) {
        // if viewOptions.el (retrieved by this.createEl()) is the template el (default) rather than a 
        // newly created element, delete it from the options and let the Backbone.View create the element
        // https://backbonejs.org/#View-el

        if (!viewOptions.tagName && viewOptions.el && viewOptions.el.tagName) {
          viewOptions.tagName = viewOptions.el.tagName.toLowerCase();
        }
        delete viewOptions.el;
      }

      var view = new ViewConstructor(viewOptions);

      this.instances.push(view);

      if (options.node) {
        
        var subViewOptions = _.extend(
          {},
          options,
          {
            parentElement: view.el,
          }
        )

        const evaluatedChild = this.evaluateGuardedChild(model);

        _.each(options.node.children, function (childNode) {
          // create views for all children which are either not guarded 
          //  or which was chosen according to the guard expressions
          if (!childNode.guard || evaluatedChild === childNode) {
            this.createSubView(childNode, subViewOptions, model);
          }
        }, this);
      }

      return view;
    },

    evaluateGuardedChild: function(model) {
      var defaultChild, evaluatedChild; 
      
      for (let i = 0; i < this.guardedChildren.length; i++) {
        const child = this.guardedChildren[i];

        if (child.guard.expression && !child.guard.isOtherwiseGuard && child.guard.evaluate(model, this.application)) {
          evaluatedChild = child;
          break; // guard expression is true, no need to iterate further
        }

        if (child.guard.isOtherwiseGuard) {
          defaultChild = child;
          // do not break yet, since the otherwise guard chould be e.g. in the middle
        }
      }

      return evaluatedChild || defaultChild;
    },

    createEl: function () {
      return this.el;
    },

    createSubView: function (childNode, options, model) {
      var view = childNode.template.createView({
        application: options.application,
        model: model,
        node: childNode
      });

      // only add it to the view if the template el was not used as view el
      if (view && childNode.template
        && view.el !== childNode.template.el
        && view.el !== childNode.template.el.parentNode) {

        // if parentElement is undefined, fallback to $(childNode.template.el) to support old logic
        if (options.parentElement === undefined || options.parentElement === childNode.parentNode.template.el) {
          // if the parentElement is the template el of the parent
          // insert it after the child template el (keep current behaviour)
          $(childNode.template.el).after(view.el);
        } else {
          // if parentElement was newly created (not the parent template el)
          // just append it.
          $(options.parentElement).append(view.el);
        }
      }

      return view;
    },

    removeViews: function() {
      if (this.instances && this.instances.remove) {
        this.instances.remove();
      }
    },

    remove: function (options) {
      this.stopListening();
      this.removeViews();
      this.instances = new Instances();
      _.forEach(options.node.children, function (childNode) {
        childNode.template.remove({
          node: childNode
        });
      });
    },

    /** Implementation detail */
    _appendChildren: function (el, parentNode) {
      var that = this,
      children = this.children;

      _.each(el.childNodes, function (childNode) {
        if (!childNode || /mb-dont-serialize/.test(childNode.className)) return;
        for (var i = 0 ; i < that.instances.instances.length; ++i) {
          if (that.instances.instances[i] && that.instances.instances[i].owns && that.instances.instances[i].owns(childNode))
            return;
        }

        var found = _.find(children, function (child) {
          if (child.el === childNode) {
            parentNode.innerHTML += child.template.serialize();
            return true;
          }
        });
        if (!found) {
          var newChildNode = childNode.cloneNode();
          that._appendChildren(childNode, newChildNode);
          parentNode.appendChild(newChildNode);
        }
      });
    },

    // serialize html => currently takes all innerHTML without
    // .mb-dont-serialize elements
    serialize: function() {
        var contentWrapper = document.createElement("div");
      this._appendChildren(this.el, contentWrapper);

      // TODO: replace existing subtemplates

      var that = this,
      html = "<" + this.el.tagName + " ",
      attributes = _.extend(
        dom.getAttributes(this.el),
        _.object(_.map(_.keys(this.get()), function (name) { return "data-" + name; }), _.values(this.get()))
      ),
      content = attributes["data-content"];

      html += _.filter(_.collect(attributes, function (value, key) {
        if (!key || !value || key === "data-content") return;
        return key + "=\"" + that.schema.serializeAttribute(value, key.substring(5) /* substring data- */) + "\""; // TODO: escape
      }), function (o) { return o; }).join(" ");

      html += ">";
      html += this.schema.content ? content : contentWrapper.innerHTML;
      html += "</" + this.el.tagName + ">";

      return html;
    }

  });

  Template.Schema = Schema;
  Template.extend = Backbone.Model.extend;

  return Template;
});
